import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { useSelectedStudent } from "@/contexts/SelectedStudentContext";
import { useProjectId } from "@/hooks/use-project-id";
import { useUserSession } from "@/hooks/use-user-session";

interface ParentSidebarVisibility {
  hasDiagnosticResults: boolean;
  hasEndOfTermResults: boolean;
  hasExamResults: boolean;
  hasAssignments: boolean;
}

export function useParentSidebarVisibility() {
  const { userRole } = useRole();
  const { data: session } = useUserSession();
  const { students } = useSelectedStudent();
  const { data: projectId } = useProjectId();
  const userId = session?.userId ?? null;

  // Sidebar visibility is based on ALL of the parent's children — it should
  // NOT change when the user switches the selected student tab, otherwise the
  // 5 parallel count queries refire on every tab click (and abort each other
  // mid-flight while the dependency settles).
  const studentIds = students.map((s) => s.id);
  const studentKey = studentIds.slice().sort().join(",");

  return useQuery<ParentSidebarVisibility>({
    queryKey: ["parent-sidebar-visibility", userId, projectId, studentKey],
    enabled: userRole === "parent" && !!projectId && !!userId && studentIds.length > 0,
    queryFn: async () => {
      if (!projectId || studentIds.length === 0) {
        return {
          hasDiagnosticResults: false,
          hasEndOfTermResults: false,
          hasExamResults: false,
          hasAssignments: false,
        };
      }

      // Run all checks in parallel — only show links when tests are COMPLETED
      const [diagResult, eotResult, examAssignmentResult, examAttemptResult, assignmentResult] = await Promise.all([
        // Check completed diagnostic test results (quiz_attempts with status=completed)
        supabase
          .from("quiz_attempts")
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId)
          .in("student_id", studentIds)
          .eq("status", "completed"),

        // Check completed end-of-term exam attempts (both end_of_term_tests table and exam_type)
        (async () => {
          const [eotTestsRes, eotExamsRes] = await Promise.all([
            supabase.from("end_of_term_tests").select("exam_id").eq("project_id", projectId).not("exam_id", "is", null),
            supabase.from("exams").select("id").eq("project_id", projectId).eq("exam_type", "end_of_term"),
          ]);
          const allEotIds = [
            ...new Set([
              ...((eotTestsRes.data || []).map((t) => t.exam_id).filter(Boolean) as string[]),
              ...(eotExamsRes.data || []).map((e) => e.id),
            ]),
          ];
          if (allEotIds.length === 0) return { count: 0 };
          const { count } = await supabase
            .from("exam_attempts")
            .select("id", { count: "exact", head: true })
            .eq("project_id", projectId)
            .in("student_id", studentIds)
            .in("exam_id", allEotIds)
            .eq("status", "completed");
          return { count };
        })(),

        // Show Exam Results as soon as a parent has an assigned exam.
        supabase
          .from("exam_assignments")
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId)
          .in("student_id", studentIds)
          .neq("status", "closed"),

        // Also show it when any saved exam section quiz result exists, even before the whole exam is complete.
        supabase
          .from("exam_attempts")
          .select("id, section_attempts:exam_section_attempts!inner(id, quiz_attempt_id, status)", { count: "exact", head: true })
          .eq("project_id", projectId)
          .in("student_id", studentIds)
          .in("status", ["completed", "in_progress"])
          .eq("section_attempts.status", "completed")
          .not("section_attempts.quiz_attempt_id", "is", null),

        // Check assignments
        supabase
          .from("assignment_assignments")
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId)
          .in("student_id", studentIds),
      ]);

      return {
        hasDiagnosticResults: (diagResult.count || 0) > 0,
        hasEndOfTermResults: (eotResult.count || 0) > 0,
        hasExamResults: (examAssignmentResult.count || 0) > 0 || (examAttemptResult.count || 0) > 0,
        hasAssignments: (assignmentResult.count || 0) > 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

